import type { FaultData } from '../api/types';

export interface SeverityInfo {
  label: string;
  color: string;
  bg: string;
}

export interface StateInfo {
  label: string;
  color: string;
}

// ── DTC code formatter ────────────────────────────────────────────────────
// Converts Geotab's numeric diagnostic code to standard OBD-II string (e.g. P0123).

export function formatDTCCode(numericCode: number | null | undefined): string | null {
  if (numericCode === null || numericCode === undefined) return null;
  const val = parseInt(String(numericCode));
  if (isNaN(val) || val <= 0) return null;
  const systems = ['P', 'C', 'B', 'U'];
  const system  = systems[(val >> 14) & 0x3];
  const subtype = (val >> 12) & 0x3;
  const specific = (val & 0xFFF).toString(16).toUpperCase().padStart(3, '0');
  return `${system}${subtype}${specific}`;
}

// ── Fault severity ────────────────────────────────────────────────────────

export function getFaultSeverity(fault: FaultData): SeverityInfo | null {
  if (fault.severity) {
    const s = fault.severity.toLowerCase();
    if (s.includes('critical')) return { label: 'CRITICAL', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' };
    if (s.includes('high'))     return { label: 'HIGH',     color: '#f97316', bg: 'rgba(249,115,22,0.15)' };
    if (s.includes('medium'))   return { label: 'MEDIUM',   color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' };
    if (s.includes('low'))      return { label: 'LOW',      color: '#10b981', bg: 'rgba(16,185,129,0.12)' };
  }
  if (fault.redStopLamp)      return { label: 'CRITICAL',     color: '#ef4444', bg: 'rgba(239,68,68,0.15)' };
  if (fault.amberWarningLamp) return { label: 'WARNING',      color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' };
  if (fault.malfunctionLamp)  return { label: 'CHECK ENGINE', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' };
  return null;
}

// ── Fault state label ─────────────────────────────────────────────────────

export function getFaultStateLabel(fault: FaultData): StateInfo | null {
  const state = (fault.faultState ?? '').toLowerCase();
  if (state.includes('active'))   return { label: 'Active',   color: '#ef4444' };
  if (state.includes('pending'))  return { label: 'Pending',  color: '#f59e0b' };
  if (state.includes('inactive')) return { label: 'Inactive', color: '#8b95a8' };
  if (Array.isArray(fault.faultStates) && fault.faultStates.length > 0) {
    const s = (fault.faultStates[0] ?? '').toLowerCase();
    if (s.includes('active'))  return { label: 'Active',  color: '#ef4444' };
    if (s.includes('pending')) return { label: 'Pending', color: '#f59e0b' };
  }
  return null;
}

// ── Display label for a fault ─────────────────────────────────────────────

export function faultLabel(fault: FaultData): string {
  const dtc  = fault.source === 'vehicle' ? formatDTCCode(fault.diagnostic?.code ?? null) : null;
  const name = fault.diagnostic?.name;
  const isUnknown = !name || name.toLowerCase().includes('unknown') || name.startsWith('**');
  if (dtc && isUnknown) return dtc;
  if (dtc && name)      return `${dtc} — ${name}`;
  if (name && !isUnknown) return name;
  return dtc ?? 'Unknown Fault';
}
